import React, { useState } from "react";
import moment from "moment";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MdDownload } from "react-icons/md";
import toast from "react-hot-toast";

const formatNum = (n) =>
  Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const formatPL = (v) => {
  const n = Number(v ?? 0);
  return n >= 0 ? formatNum(n) : `-${formatNum(Math.abs(n))}`;
};

const ReportDownload = ({
  reportList,
  fromDate,
  toDate,
  disabled,
  userName = "",
}) => {
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = () => {
    if (!reportList?.length) {
      toast.error("No report data to download");
      return;
    }
    setDownloading(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 20;
      const maxY = pageHeight - bottomMargin;
      let y = 15;

      doc.setFontSize(14);
      doc.text("Report", pageWidth / 2, y, { align: "center" });
      y += 8;
      doc.setFontSize(10);
      doc.text(
        `User Name: ${userName} | From: ${fromDate} to To: ${toDate}`,
        pageWidth / 2,
        y,
        { align: "center" },
      );
      y += 14;

      const receiptGridRows = 4;
      const receiptsSectionHeight =
        14 + 4 + receiptGridRows * 14 + 2 + 2 + 8 + 2;
      // Header row + one row per game + the total row.
      const maxGameRows = Math.max(
        1,
        ...reportList.map((r) => (r.games || []).length),
      );
      const gameTableHeight = 10 + (maxGameRows + 2) * 8 + 14;
      const fullSectionHeight = receiptsSectionHeight + 6 + gameTableHeight;

      const checkPageBreak = (neededHeight) => {
        if (y + neededHeight > maxY) {
          doc.addPage();
          y = 15;
        }
      };

      reportList.forEach((row) => {
        const txnDate = moment(row.txn_date).format("DD MMM YYYY");
        const hasGameTable =
          (row.user_role_id === 2 || row.user_role_id === 5) ||
          row.user_role_id === 3 ||
          row.user_role_id === 4;
        const sectionHeight = hasGameTable
          ? fullSectionHeight
          : receiptsSectionHeight;
        checkPageBreak(sectionHeight);

        const colW = (pageWidth - 28) / 2;
        const rowH = 14;

        doc.setFillColor(102, 126, 234);
        doc.rect(14, y - 4, pageWidth - 28, 10, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text(txnDate, 20, y + 2);
        doc.setTextColor(0, 0, 0);
        y += 2;

        doc.setFontSize(9);
        doc.setFont(undefined, "bold");
        doc.setTextColor(71, 85, 105);
        doc.text("Receipts", 14 + colW / 2, y, { align: "center" });
        doc.text("Payments", 14 + colW + colW / 2, y, { align: "center" });
        doc.setTextColor(0, 0, 0);
        y += 4;

        const receiptPayGrid = [
          [
            ["Admin Receipt", row.admin_receipt_amount || 0],
            ["Admin Payment", row.admin_payment_amount || 0],
          ],
          [
            ["Super Admin Receipt", row.super_admin_receipt_amount || 0],
            ["Super Admin Payment", row.super_admin_payment_amount || 0],
          ],
          [
            ["Outlet Receipt", row.outlet_receipt_amount || 0],
            ["Outlet Payment", row.outlet_payment_amount || 0],
          ],
          [
            ["Mobile Receipt", row.mobile_receipt_amount || 0],
            ["Mobile Payment", row.mobile_payment_amount || 0],
          ],
        ];

        receiptPayGrid.forEach((gridRow, ri) => {
          const leftX = 14;
          const rightX = 14 + colW;
          const cellY = y + ri * rowH;

          [0, 1].forEach((col) => {
            const [label, val] = gridRow[col];
            const x = col === 0 ? leftX : rightX;
            doc.setDrawColor(203, 213, 225);
            doc.setLineWidth(0.2);
            doc.rect(x, cellY, colW, rowH, "S");
            doc.setFontSize(8);
            doc.setFont(undefined, "normal");
            doc.setTextColor(115, 115, 115);
            doc.text(label, x + 4, cellY + 6);
            doc.setFontSize(11);
            doc.setFont(undefined, "bold");
            doc.setTextColor(0, 0, 0);
            doc.text(formatNum(val), x + colW / 2, cellY + 11, {
              align: "center",
            });
          });
        });
        y += receiptPayGrid.length * rowH + 2;

        if (
          (row.user_role_id === 2 || row.user_role_id === 5) ||
          row.user_role_id === 3 ||
          row.user_role_id === 4
        ) {
          y += 3;
          doc.setFontSize(9);
          doc.setFont(undefined, "normal");
          doc.setTextColor(71, 85, 105);
          doc.text(
            `Opening Balance: ${formatNum(row.user_opening_balance)}`,
            14,
            y,
          );
          doc.text(
            `Closing Balance: ${formatNum(row.user_closing_balance)}`,
            pageWidth - 14,
            y,
            { align: "right" },
          );
          doc.setTextColor(0, 0, 0);
          y += 2;

          checkPageBreak(gameTableHeight + 10);

          const tableW = pageWidth - 28;
          doc.setFillColor(20, 184, 166);
          doc.rect(14, y, tableW, 8, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(9);
          doc.setFont(undefined, "bold");
          doc.text("Game", 14 + tableW * 0.1, y + 5.5);
          doc.text("Purchase", 14 + tableW * 0.3, y + 5.5);
          doc.text("Win", 14 + tableW * 0.5, y + 5.5);
          doc.text("Cancel", 14 + tableW * 0.7, y + 5.5);
          doc.text("P/L", 14 + tableW * 0.9, y + 5.5);
          doc.setTextColor(0, 0, 0);
          y += 10;

          // One line per game the row carries, then the total.
          const gameData = [
            ...(row.games || []).map((g) => [
              g.game_name || g.game_code,
              g.total_purchase_amount || 0,
              g.total_win_amount || 0,
              g.total_cancel_amount || 0,
              g.profit_loss ?? 0,
            ]),
            [
              "Total",
              row.total_data?.total_purchase_amount || 0,
              row.total_data?.total_win_amount || 0,
              row.total_data?.total_cancel_amount || 0,
              row.total_data?.overall_profit_loss ?? 0,
            ],
          ];

          autoTable(doc, {
            startY: y,
            body: gameData.map(([game, p, w, c, pl]) => [
              game,
              formatNum(p),
              formatNum(w),
              formatNum(c),
              formatPL(pl),
            ]),
            theme: "grid",
            margin: { left: 14 },
            tableWidth: tableW,
            columnWidths: [
              tableW * 0.2,
              tableW * 0.2,
              tableW * 0.2,
              tableW * 0.2,
              tableW * 0.2,
            ],
            columnStyles: {
              0: { cellWidth: tableW * 0.2 },
              1: { halign: "right" },
              2: { halign: "right" },
              3: { halign: "right" },
              4: { halign: "right" },
            },
            styles: { fontSize: 8 },
          });
          y = doc.lastAutoTable.finalY + 10;
        } else {
          y += 5;
        }
      });

      const fileName = `report_(${userName})_${fromDate}_to_${toDate}.pdf`;
      doc.save(fileName);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const isDisabled = disabled || !reportList?.length;

  return (
    <div className="report-download-wrap">
      <div className="report-download-hover-wrap">
        <button
          type="button"
          className="report-download-trigger"
          onClick={downloadPDF}
          disabled={isDisabled}
          title="Download PDF"
        >
          <MdDownload size={20} />
          <span>{downloading ? "…" : "PDF"}</span>
        </button>
      </div>
    </div>
  );
};

export default ReportDownload;
